import React, { useCallback } from 'react';
import ReactDom from 'react-dom';
import Select from 'react-select';
import {
  Button,
  Form,
} from 'react-bootstrap';
import {
  useSelector
} from 'react-redux';
import { Formik } from 'formik';
import { useDispatch } from "react-redux";
import {
  propOr
} from 'ramda';
import { ProgressBar } from './ProgressBar';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FormError } from "./fields/FormError";
import classNames from 'classnames';
import step2Validations from './step2Validations';
import {
  withinSuccessCodeRange,
  isEnterKey,
  getCsrfToken,
  } from '@utils';
import {
  INFLUENCER_APPLICATIONS_URL
} from '@endpoints'
import {
  formatInfluencerApplication
} from '@formatters';


import {
  genderOptions,
  zodiacOptions,
  bodyTypeOptions,
  eyeColorOptions,
  hairColorOptions,
  heightOptions,
  experienceOptions,
} from './influencerOptions';
import {
  setServerError
} from '@actions';

const yup = require("yup");

const validationSchema = yup.object(step2Validations);



const Step2 = ({ goToPreviousStep, goToNextStep }) => {
  const dispatch = useDispatch();

   const influencerApplication = useSelector(state => {
    return state.influencerApplication
  });

  const handlePreviousStepClick = () => {
    goToPreviousStep();
  }

  const preventSubmitOnEnterKeydown = (keyEvent) => {
      keyEvent.persist()
      if (isEnterKey(keyEvent)) {
        keyEvent.preventDefault();
      }
    }

  const setFieldValueOnChange = (setFieldValue, key) => (val) => {
    setFieldValue(key, val)
  }

  const setFieldValueOnOptionChange = (setFieldValue, key) => (option) => {
    setFieldValue(key, option.value)
  }

  const handleSubmitting = async (formData) => {
    const formattedData = formatInfluencerApplication(formData);
    const res = await fetch(INFLUENCER_APPLICATIONS_URL, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
      },
      body: JSON.stringify(formattedData)
    })
    const status = res.status;
    if(withinSuccessCodeRange(status)) {
      goToNextStep();
    }
    else {
      const json = await res.json();
      const error = propOr('An error occurred. Try re-submitting your application', 'error', json);
      dispatch(setServerError(error));
    }
  }


  return (
     <Formik
      validateOnChange={false}
      validateOnBlur
      validationSchema={validationSchema}
      onSubmit={async (
        data,
        { setFieldValue, setErrors, setSubmitting }
      ) => {
        handleSubmitting({
          ...data,
          ...influencerApplication
        });
      }}
      initialValues={{
        birthday: "",
        gender: "",
        bodyType: "",
        bustSize: "",
        weight: "",
        eyeColor: "",
        zodiacSign: "",
        hairColor: "",
        height: "",
        experience: ""
      }}
      >
      {({
        handleSubmit,
        handleChange,
        values,
        errors,
        setFieldValue,
        isSubmitting,
        setErrors
      }) => (
      <div>
         <Form
            noValidate
            onSubmit={data => {
              data.preventDefault();
              handleSubmit();
            }}
            onKeyDown={preventSubmitOnEnterKeydown}
          >
          <Form.Group>
            <Form.Label className="form-control-label">
              Birthday
            </Form.Label>
            <DatePicker
              selected={values.birthday}
              className="form-control"
              placeholderText="Select your birthday"
              value={values.birthday}
              required
              onChange={setFieldValueOnChange(setFieldValue, 'birthday')}
            />
            { errors.birthday ? <FormError error={errors.birthday} /> : null }
          </Form.Group>
          <Form.Group>
            <Form.Label className="form-control-label">
              Gender
            </Form.Label>
            <Select
              options={genderOptions}
              name="gender"
              value={genderOptions.filter((option) => option.value === values.gender)}
              onChange={setFieldValueOnOptionChange(setFieldValue, 'gender')}
            />
            { errors.gender ? <FormError error={errors.gender} /> : null }
          </Form.Group>
          <Form.Group>
            <Form.Label className="form-control-label">
              Body Type
            </Form.Label>
            <Select
              options={bodyTypeOptions}
              name="bodyType"
              value={bodyTypeOptions.filter((option) => option.value === values.bodyType)}
              onChange={setFieldValueOnOptionChange(setFieldValue, 'bodyType')}
            />
            { errors.bodyType ? <FormError error={errors.bodyType} /> : null }
          </Form.Group>
           <Form.Group>
            <Form.Label className="form-control-label">
              Bust Size
            </Form.Label>
            <Form.Control
              name="bustSize"
              type="text"
              placeholder="32A"
              className={errors.bustSize ? "form-control error" : "form-control"}
              value={values.bustSize}
              onChange={(e) => {
                e.persist()
                handleChange(e)
              }}
              required
            />
            { errors.bustSize ? <FormError error={errors.bustSize} /> : null }
          </Form.Group>
           <Form.Group>
            <Form.Label className="form-control-label">
              Weight
            </Form.Label>
            <Form.Control
              name="weight"
              type="number"
              placeholder="How much do you weigh in pounds?"
              className={classNames("form-control", {"error": errors.weight && errors.weight.length > 0})}
              value={values.weight}
              onChange={(e) => {
                e.persist()
                handleChange(e)
              }}
              required
            />
            { errors.weight ? <FormError error={errors.weight} /> : null }
          </Form.Group>
           <Form.Group>
            <Form.Label className="form-control-label">
              Eye Color
            </Form.Label>
            <Select 
              options={eyeColorOptions}
              name="eyeColor"
              value={eyeColorOptions.filter(function(option) {
                return option.value === values.eyeColor;
              })}
              onChange={setFieldValueOnOptionChange(setFieldValue, 'eyeColor')}
            />
            { errors.eyeColor ? <FormError error={errors.eyeColor} /> : null }
          </Form.Group>
           <Form.Group>
            <Form.Label className="form-control-label">
              Zodiac Sign
            </Form.Label>
            <Select
              options={zodiacOptions} 
              name="zodiacSign"
              value={zodiacOptions.filter(function(option) {
                return option.value === values.zodiacSign;
              })}
              onChange={setFieldValueOnOptionChange(setFieldValue, 'zodiacSign')}
            />
            { errors.zodiacSign ? <FormError error={errors.zodiacSign} /> : null }
          </Form.Group>
           <Form.Group>
            <Form.Label className="form-control-label">
              Hair Color
            </Form.Label>
            <Select
              options={hairColorOptions}
              name="hairColor"
              value={hairColorOptions.filter(function(option) {
                return option.value === values.hairColor;
              })}
              onChange={setFieldValueOnOptionChange(setFieldValue, 'hairColor')}
            />
            { errors.hairColor ? <FormError error={errors.hairColor} /> : null }
          </Form.Group>
           <Form.Group>
            <Form.Label className="form-control-label">
              Height
            </Form.Label>
            <Select
              options={heightOptions}
              name="height"
              value={heightOptions.filter(function(option) {
                return option.value === values.height;
              })}
              onChange={setFieldValueOnOptionChange(setFieldValue, 'height')}
            />
            { errors.height ? <FormError error={errors.height} /> : null }
          </Form.Group>
           <Form.Group>
            <Form.Label className="form-control-label">
              Do you have any experience in the adult industry or similar business?
            </Form.Label>
            <Select
              options={experienceOptions}
              name="experience"
              value={experienceOptions.filter(function(option) {
                return option.value === values.experience;
              })}
              onChange={setFieldValueOnOptionChange(setFieldValue, 'experience')}
            />
            { errors.experience ? <FormError error={errors.experience} /> : null }
          </Form.Group>
          <Button type="submit" className='mb-3' variant='light' onClick={handlePreviousStepClick}>Previous</Button>
          <Button type="submit" className="btn pink-button">Continue</Button>
        </Form>
      </div>
  )}
  </Formik>
  )
}

export default Step2;